//ApplicationsRequiringAttention
import { Button, Form, Pagination, Table } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useDispatch, useSelector } from "react-redux"
import { camelCaseToWords, downloadFile, formatDate, getApplicationStatusSelectValue } from "../../../constant/utils"
import Loader from "../../../components/Loader"
import { css } from "@emotion/react"
import { useEffect, useState } from "react"
import {
    changeApplicationStatus,
    downloadQuotePDF,
    getApplicationsRequiringAttention
} from "../../../data/applicationSlice"
import { getApplicationStatus } from "../../../data/enumSlice"
import { byPrefixAndName } from "@awesome.me/kit-de80aecb6a/icons"

const componentStyle = css`
    p.note {
        color: #333;
        background: gold;
        font-size: 14px;
        margin: 12px 0 0 0;
    }
`

const ApplicationsRequiringAttention = ({ handleInformationShow, handleOpenModal, handleComments }) => {
    const dispatch = useDispatch()

    const { applicationsRequiringAttention, fetchStatus, applicationsRequiringAttentionRecords } = useSelector((state) => state.application)
    const { applicationStatus } = useSelector((state) => state.enums)
    const [activePage, setActivePage] = useState(0)
    const [isChangingPage, setIsChangingPage] = useState(false)

    const numberOfRecordsPerPage = 25
    const totalPages = Math.ceil(applicationsRequiringAttentionRecords / numberOfRecordsPerPage)

    useEffect(() => {
        !applicationStatus?.length && dispatch(getApplicationStatus())
    }, [])

    useEffect(() => {
        setActivePage(0)
        getApplicationsRequiringAttentionList()
    }, [])

    const handlePageChange = async (index) => {
        if (index >= 0 && index < totalPages && index !== activePage && !isChangingPage) {
            setIsChangingPage(true)
            setActivePage(index)
            try {
                await getApplicationsRequiringAttentionList(index + 1)
            } catch (error) {
                console.error('Error changing page:', error)
                // Revert to previous page on error
                setActivePage(activePage)
            } finally {
                setIsChangingPage(false)
            }
        }
    }

    const getApplicationsRequiringAttentionList = (pageIndex = 1) => {
        if (pageIndex < 1) pageIndex = 1
        return dispatch(
            getApplicationsRequiringAttention({
                pageIndex,
                numberOfRecords: numberOfRecordsPerPage,
                orderBy: "firstNamedInsured",
                descending: true
            })
        )
    }

    const changeStatus = (id, status) => {
        if (!status) return
        dispatch(changeApplicationStatus({ id, status })).then(({ payload }) => {
            if (payload?.succeeded) {
                getApplicationsRequiringAttentionList(activePage + 1)
            }
        })
    }

    const downloadApplicationQuote = (id) => {
        dispatch(downloadQuotePDF(id)).then(({ payload }) => {
            if (payload?.file) {
                downloadFile(payload)
            }
        })
    }

    const renderPagination = () => {
        const paginationItems = []
        const ellipsis = (
            <Pagination.Ellipsis
                key="ellipsis"
                disabled
            />
        )
        const pagesToShow = 5 // Number of pages to show in the middle
        const tailPagesToShow = 2 // Number of pages to show at the end

        // Add First and Previous Buttons
        paginationItems.push(
            <Pagination.First
                key="first"
                onClick={() => handlePageChange(0)}
                disabled={activePage === 0}
            />
        )
        paginationItems.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(activePage - 1)}
                disabled={activePage === 0}
            />
        )

        if (totalPages <= pagesToShow + tailPagesToShow) {
            // If total pages are small, show all pages
            for (let i = 0; i < totalPages; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }
        } else {
            // Show first page
            paginationItems.push(
                <Pagination.Item
                    key={0}
                    onClick={() => handlePageChange(0)}
                    active={activePage === 0}>
                    {1}
                </Pagination.Item>
            )

            // Determine start and end of the middle range
            const startPage = Math.max(1, activePage - Math.floor((pagesToShow - 1) / 2))
            const endPage = Math.min(totalPages - tailPagesToShow, startPage + pagesToShow - 1)

            // Add ellipsis before middle pages if needed
            if (startPage > 1) {
                paginationItems.push(ellipsis)
            }

            // Add middle pages
            for (let i = startPage; i < endPage; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }

            // Add ellipsis after middle pages if needed
            if (endPage < totalPages - tailPagesToShow) {
                paginationItems.push(ellipsis)
            }

            // Show last few pages
            for (let i = totalPages - tailPagesToShow; i < totalPages; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }
        }

        // Add Next and Last Buttons
        paginationItems.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(activePage + 1)}
                disabled={activePage === totalPages - 1}
            />
        )
        paginationItems.push(
            <Pagination.Last
                key="last"
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={activePage === totalPages - 1}
            />
        )

        return paginationItems
    }

    return (
        <div css={componentStyle}>
            <Loader show={fetchStatus === "loading" || isChangingPage} />
            <h2>Applications Requiring Attention</h2>
            {applicationsRequiringAttention?.length === 0 && fetchStatus === "success" && (
                <p className="mt-4 pt-3 text-center h5">No Results</p>
            )}
            {applicationsRequiringAttention?.length > 0 && (
                <>
                    <Table
                        striped
                        bordered
                        hover
                        responsive
                        className="centered-row mt-4">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>#</th>
                                <th>First Named Insured</th>
                                <th>Agency Name</th>
                                <th>Agency Contact</th>
                                <th>Date Started</th>
                                <th>Date Submitted</th>
                                <th>Status</th>
                                <th>Application</th>
                                <th>Change Status</th>
                                <th>View Comments</th>
                                <th>Bind Request Date</th>
                                <th>Quote PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applicationsRequiringAttention?.map((application, index) => {
                                return (
                                    <tr key={index}>
                                        <td>{application?.friendlyId}</td>
                                        <td>{application?.customerNumber || ""}</td>
                                        <td>{application?.firstNamedInsured}</td>
                                        <td className="cursor-pointer">
                                            {application?.agencyName}
                                            {application?.agencyName && (
                                                <FontAwesomeIcon
                                                    onClick={() =>
                                                        handleInformationShow("Agency", application?.agencyId)
                                                    }
                                                    className="mx-2 cursor-pointer"
                                                    icon={byPrefixAndName.fal["circle-info"]}
                                                />
                                            )}
                                        </td>
                                        <td className="">
                                            {application?.agencyContact}
                                            {application?.agencyContact && (
                                                <FontAwesomeIcon
                                                    onClick={() =>
                                                        handleInformationShow("Contact", application?.agencyContactId)
                                                    }
                                                    className="cursor-pointer mx-2"
                                                    icon={byPrefixAndName.fal["circle-info"]}
                                                />
                                            )}
                                        </td>
                                        <td>{formatDate(application?.dateStarted)}</td>
                                        <td>{formatDate(application?.dateSubmitted)}</td>
                                        <td>{camelCaseToWords(application?.status)}</td>
                                        <td style={{ textAlign: "center" }}>
                                            <Button
                                                onClick={() => handleOpenModal(application?.id)}
                                                variant="light"
                                                className="me-2 btn-sm">
                                                <FontAwesomeIcon icon={byPrefixAndName.fal["magnifying-glass"]} />
                                            </Button>
                                        </td>
                                        <td>
                                            <Form.Select
                                                name="status"
                                                className="form-select-sm"
                                                onChange={(e) => changeStatus(application?.id, e.target.value)}
                                                value={getApplicationStatusSelectValue(
                                                    applicationStatus,
                                                    application?.status
                                                )}>
                                                <option value={""}>--- Select ---</option>
                                                {applicationStatus?.map((status, i) => (
                                                    <option
                                                        key={i}
                                                        value={status?.name}>
                                                        {status?.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </td>
                                        <td
                                            onClick={() => handleComments(application?.id)}
                                            className="text-primary h6"
                                            role="button">
                                            Comments
                                        </td>
                                        <td>
                                            {application?.bindRequestDate
                                                ? formatDate(application?.bindRequestDate)
                                                : "N/A"}
                                        </td>

                                        <td style={{ textAlign: "center" }}>
                                            <Button
                                                variant="light"
                                                className="me-2 btn-sm"
                                                onClick={() => downloadApplicationQuote(application?.id)}>
                                                <FontAwesomeIcon icon={byPrefixAndName.fal["download"]} />
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                    {totalPages > 1 && (
                        <Pagination
                            className="mt-4 d-flex justify-content-end"
                            size="md">
                            {renderPagination()}
                        </Pagination>
                    )}
                </>
            )}
        </div>
    )
}

export default ApplicationsRequiringAttention
